/* ============================================================
   FormPage — 익명 공개 폼 (로그인 불필요)
   라우트: /form/:token  (오케스트레이터가 App.jsx 에 추가)
   - get_public_form(p_token) → { ok, title, description, columns }
   - submit_form(p_token, p_values) → { ok }
   useAuth 사용 금지 (anon 접근).
   ============================================================ */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import './form-page.css';

/* 컬럼 타입별 입력 필드 */
function FormField({ col, value, onChange }) {
  const type = col.type;

  if (type === 'select') {
    return (
      <select
        className="fp-input"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">선택하세요</option>
        {(col.options || []).map((o) => {
          const name = typeof o === 'string' ? o : o.name;
          return <option key={name} value={name}>{name}</option>;
        })}
      </select>
    );
  }

  if (type === 'date') {
    return (
      <input
        className="fp-input"
        type="date"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (type === 'checkbox') {
    return (
      <label className="fp-check">
        <input
          type="checkbox"
          checked={value === true || value === 'true'}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>예</span>
      </label>
    );
  }

  if (type === 'number') {
    return (
      <input
        className="fp-input"
        type="number"
        value={value ?? ''}
        placeholder="숫자 입력"
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      />
    );
  }

  if (type === 'url') {
    return (
      <input
        className="fp-input"
        type="url"
        value={value ?? ''}
        placeholder="https://"
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  // title / text / person / 기타 → 텍스트 입력
  return (
    <input
      className="fp-input"
      type="text"
      value={value ?? ''}
      placeholder="입력하세요"
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export default function FormPage() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // 'not_found' | 'unknown'
  const [form, setForm] = useState(null);   // { title, description, columns }
  const [values, setValues] = useState({}); // { [key]: value }
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setForm(null);
    if (!token) { setError('not_found'); setLoading(false); return () => { active = false; }; }
    (async () => {
      const { data, error: rpcErr } = await supabase.rpc('get_public_form', { p_token: token });
      if (!active) return;
      if (rpcErr) {
        setError('unknown');
      } else if (!data || data.ok === false) {
        setError((data && data.error) || 'not_found');
      } else {
        setForm(data);
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [token]);

  const setValue = (key, v) => setValues((prev) => ({ ...prev, [key]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSubmitErr('');
    const { data, error: rpcErr } = await supabase.rpc('submit_form', {
      p_token: token,
      p_values: values,
    });
    setSubmitting(false);
    if (rpcErr) {
      setSubmitErr(rpcErr.message || '제출에 실패했습니다. 잠시 후 다시 시도해 주세요.');
      return;
    }
    if (!data || data.ok === false) {
      setSubmitErr((data && data.error) || '제출에 실패했습니다. 잠시 후 다시 시도해 주세요.');
      return;
    }
    setDone(true);
  };

  if (loading) {
    return (
      <div className="fp-page">
        <div className="fp-card fp-state">
          <div className="spinner" />
          <div className="fp-muted">폼을 불러오는 중…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fp-page">
        <div className="fp-card fp-state">
          <div className="fp-state-icon">🔒</div>
          <div className="fp-state-title">폼을 찾을 수 없습니다</div>
          <div className="fp-state-sub">링크가 잘못되었거나 게시가 해제되었습니다.</div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="fp-page">
        <div className="fp-card fp-state">
          <div className="fp-state-icon">✅</div>
          <div className="fp-state-title">제출되었습니다</div>
          <div className="fp-state-sub">응답해 주셔서 감사합니다.</div>
          <div className="fp-foot">
            <img className="fp-logo" src="/logo-wordmark.png" alt="루나르트" />
          </div>
        </div>
      </div>
    );
  }

  const columns = Array.isArray(form?.columns) ? form.columns : [];

  return (
    <div className="fp-page">
      <form className="fp-card" onSubmit={submit}>
        <img className="fp-logo-top" src="/logo-wordmark.png" alt="루나르트" />
        <h1 className="fp-title">{form?.title || '폼'}</h1>
        {form?.description && <p className="fp-desc">{form.description}</p>}

        {columns.length === 0 ? (
          <div className="fp-muted" style={{ padding: '24px 0' }}>표시할 질문이 없습니다.</div>
        ) : (
          columns.map((col) => (
            <div className="fp-field" key={col.key}>
              <label className="fp-label">{col.name}</label>
              <FormField
                col={col}
                value={values[col.key]}
                onChange={(v) => setValue(col.key, v)}
              />
            </div>
          ))
        )}

        {submitErr && <div className="fp-err">{submitErr}</div>}

        <button className="fp-submit" type="submit" disabled={submitting || columns.length === 0}>
          {submitting ? '제출 중…' : '제출'}
        </button>

        <div className="fp-foot">
          <img className="fp-logo" src="/logo-wordmark.png" alt="루나르트" />
          <span className="fp-foot-txt">루나르트로 만든 폼</span>
        </div>
      </form>
    </div>
  );
}
